/* eslint-disable react/require-default-props */
import React, {Children} from 'react';
import {get, isNil} from 'lodash';
import {flow, isEmpty, map} from 'lodash/fp';

import ProfileAttribute from '/component/base/ProfileAttribute';
import ProfileSection from '/component/partial/profile/ProfileSection';
import {IconNameByType} from '/component/partial/UrlDetailsPopover/UrlDetailsPopover.util';
import {DispositionPopover} from '/component/screen/CandidateProfile/Disposition/DispositionPopover';
import {DownlineProfile, UserProfileModal} from '/constant/TestIDs';
import downlineProfileTestIDs from '/constant/TestIDs/DownlineProfile';
import {withTranslation} from '/provider/TranslationProvider';
import {showAlert} from '/util/alert.util';
import {generateCallback} from '/util/component.util';

import {useProfileContactSection} from './ProfileContactSection.hook';
import {dispositionColorByName, styles} from './ProfileContactSection.style';
import {
  formatEmail,
  formatPhoneNumber,
  generateOpenSocials,
  generateShowOptionAlert,
  onPress,
} from './ProfileContactSection.util';

interface ProfileContactSectionProps {
  tt?: (key: any, value: any) => any;
  coachId?: number | null;
  phoneNumber?: string;
  accountEmail?: string;
  userId?: number | null;
  candidateId?: any;
  urlDetails?: Array<{type: string; url: string}>;
  popoverShow?: (component: React.ComponentType<any>, props: any) => void;
  currentScreen?: string;
  hideSocials?: boolean;
  maskContacts?: boolean;
  style?: {base: object; title: object};
  dispositionTypeId?: string;
  saveDispositionData?: (data: any) => void;
  dispositionDescription?: string;
  dispositionOptionArray?: string[];
  isPendingAcceptance?: boolean;
  isCandidate?: boolean;
  lastPaidRank?: string;
  highestAchievedRank?: string;
}

export const ProfileContactSection = ({
  tt = withTranslation.ttDefaultProp,
  coachId = null,
  phoneNumber = '',
  accountEmail = '',
  userId = null,
  urlDetails = [],
  popoverShow = () => {},
  currentScreen = '',
  candidateId = '',
  hideSocials = false,
  maskContacts = false,
  style: styleProp = {base: {}, title: {}},
  dispositionTypeId = '',
  saveDispositionData = () => {},
  dispositionDescription = '',
  dispositionOptionArray = [],
  isPendingAcceptance = false,
  isCandidate = false,
  lastPaidRank = '',
  highestAchievedRank = '',
}: ProfileContactSectionProps) => {
  const isDownline = userId !== coachId;
  const {phoneActionButtons} = useProfileContactSection({
    onPress,
    phoneNumber,
    accountEmail,
    candidateId,
    lastPaidRank,
  });

  const showPhoneOptionAlert = generateShowOptionAlert({
    actionButtons: phoneActionButtons,
    showAlert,
  });

  const {emailActionButtons} = useProfileContactSection({
    onPress,
    phoneNumber,
    accountEmail,
    candidateId,
    lastPaidRank,
  });

  const showEmailOptionAlert = generateShowOptionAlert({
    actionButtons: emailActionButtons,
    showAlert,
  });

  const renderSocials = (urlDetail: any) => (
    <ProfileAttribute
      isArrow
      // @ts-expect-error TS(2322): Type 'any[]' is not assignable to type 'string'.
      iconName={[IconNameByType[urlDetail.type]]}
      label={tt(
        `${urlDetail.type ? urlDetail.type : 'N/A'}`,
        `${urlDetail.type ? urlDetail.type : 'N/A'}`,
      )}
      value={`${urlDetail.url ? urlDetail.url : 'N/A'}`}
      style={{base: styles.profileAttribute}}
      onPress={generateOpenSocials(
        {popoverShow},
        currentScreen,
        get(urlDetail, 'url', ''),
      )}
      testID={
        urlDetail.type ? urlDetail.type : DownlineProfile.candidateLinkDetail
      }
    />
  );

  return (
    <ProfileSection
      analyticsTitle="Contact"
      style={{
        base: {...styles.base, ...styleProp?.base},
        title: {...styleProp?.title},
      }}>
      {!isPendingAcceptance && isCandidate && (
        <ProfileAttribute
          style={{
            base: {
              ...styles.profileAttribute,
              backgroundColor:
                dispositionColorByName[`${dispositionDescription}`],
            },
          }}
          showIcon={false}
          label={tt('disposition.label', 'Current Disposition')}
          testID={downlineProfileTestIDs.profileDisposition}
          value={dispositionDescription}
          iscandidateDisposition={true}
          onPress={() =>
            popoverShow(DispositionPopover, {
              dispositionOptionArray: dispositionOptionArray,
              dispositionTypeId: dispositionTypeId,
              saveDispositionData: saveDispositionData,
              candidateId: candidateId,
              dispositionDescription: dispositionDescription,
              lastPaidRank: lastPaidRank,
              highestAchievedRank: highestAchievedRank,
              coachId: userId,
            })
          }
        />
      )}
      <ProfileAttribute
        testID={UserProfileModal.profilePhone}
        iconName="phone"
        isDisabled={
          maskContacts ||
          !isDownline ||
          isEmpty(phoneNumber) ||
          isNil(phoneNumber)
        }
        label={tt('phone.label', 'Phone')}
        value={`${formatPhoneNumber(phoneNumber, maskContacts)}`}
        style={{base: styles.profileAttribute}}
        onPress={generateCallback({
          func: showPhoneOptionAlert,
          params: {phoneNumber},
        })}
      />
      <ProfileAttribute
        testID={UserProfileModal.profileEmail}
        iconName="email"
        isDisabled={
          maskContacts ||
          !isDownline ||
          isEmpty(accountEmail) ||
          isNil(accountEmail)
        }
        label={tt('email.label', 'Email')}
        value={`${formatEmail(accountEmail, maskContacts)}`}
        style={{base: styles.profileAttribute}}
        onPress={generateCallback({
          func: showEmailOptionAlert,
          params: {accountEmail},
        })}
      />
      {!hideSocials && flow([map(renderSocials), Children.toArray])(urlDetails)}
    </ProfileSection>
  );
};

export default ProfileContactSection;
